import React, { useState, FormEvent, useEffect } from 'react';
import './InputForm.css';
import { apiFetch } from '../utils/api';

interface InputFormProps {
  onSubmitSuccess: (processId: string) => void;
  onExisting: (id: string) => void;
  onError: (errorMessage: string) => void;
  initialUrl?: string;
  onUrlChange?: (url: string) => void;
  autoSubmit?: boolean;
  onAutoSubmitComplete?: () => void;
}

const InputForm: React.FC<InputFormProps> = ({ onSubmitSuccess, onExisting, onError, initialUrl, onUrlChange, autoSubmit, onAutoSubmitComplete }) => {
  const [url, setUrl] = useState<string>(initialUrl || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (initialUrl) {
      setUrl(initialUrl);
    }
  }, [initialUrl]);

  // Auto-submit when autoSubmit is true and URL is available
  useEffect(() => {
    console.log('Auto-submit effect triggered:', { autoSubmit, url: url.trim(), isLoading });
    if (autoSubmit && url.trim() && !isLoading) {
      console.log('Triggering auto-submission for URL:', url);
      handleSubmitInternal();
      onAutoSubmitComplete?.();
    }
  }, [autoSubmit, url]);

  const handleSubmitInternal = async (): Promise<void> => {
    if (!url.trim()) {
      onError('Please enter a URL');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process link');
      }

      const data = await response.json();
      if (data.existing) {
        setUrl('');
        setIsLoading(false);
        onExisting(data.id);
      } else {
        onSubmitSuccess(data.id);
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'An unknown error occurred');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    await handleSubmitInternal();
  };

  return (
    <section className="input-section">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <div className="input-with-icon">
            <i className="fas fa-link"></i>
            <input
              type="url"
              id="link-url"
              name="url"
              placeholder="Paste any link..."
              required
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                onUrlChange?.(e.target.value);
              }}
              disabled={isLoading}
            />
          </div>
        </div>
        <button type="submit" className="app-button" disabled={isLoading}>
          <span className="button-text">
            {isLoading ? 'Processing...' : 'Process Link'}
          </span>
          {isLoading && (
            <span className="spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </span>
          )}
        </button>
      </form>
    </section>
  );
};

export default InputForm;
